/**
 * Routing, and the query state that belongs to a screen.
 *
 * Nine destinations, from V1 section 9. The table is data rather than a regex
 * per screen so that a path this build does not serve is recognisably "not
 * found" instead of silently falling through to the library.
 *
 * Pure module: no DOM, no history. `main.ts` owns the browser binding and calls
 * `matchRoute` on every navigation, which is what makes the whole router
 * testable without a browser.
 */

export type Route =
  /**
   * `/` — the landing page, which IS the sign-in.
   *
   * A real route rather than "whatever `/` fell through to", because `/` used
   * to match `not-found` and the two answers were indistinguishable: a signed
   * out reader was told the page did not exist and, three lines below, that it
   * was available once they signed in. That is the same defect `views/gate.ts`
   * already documents about the screen this route replaced.
   */
  | { readonly name: 'landing' }
  | { readonly name: 'login' }
  | { readonly name: 'invite'; readonly token: string }
  | { readonly name: 'callback' }
  | { readonly name: 'skills'; readonly org: string }
  | { readonly name: 'skill'; readonly org: string; readonly id: string }
  | { readonly name: 'skill-edit'; readonly org: string; readonly id: string }
  | { readonly name: 'review'; readonly org: string; readonly draftId: string }
  | { readonly name: 'toolkits'; readonly org: string }
  | { readonly name: 'improvements'; readonly org: string }
  | { readonly name: 'connect'; readonly org: string }
  /**
   * The consent page a person is sent to when a client asks for authority.
   * Four segments, so it needs its own arm ABOVE the bare `parts.length === 3`
   * section block below — that block is reached first for any three-segment
   * path, and `authorize` under `connect` is a path this build serves.
   */
  | { readonly name: 'authorize'; readonly org: string }
  | { readonly name: 'admin'; readonly org: string }
  | { readonly name: 'not-found'; readonly path: string };

/** `:org` is a slug (V1 section 5 fixes the slug format); ids are uuids. */
const SLUG = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isSlug(value: string): boolean {
  return SLUG.test(value);
}

function isUuid(value: string): boolean {
  return UUID.test(value);
}

export function matchRoute(pathname: string): Route {
  const parts = pathname.split('/').filter((part) => part !== '');
  // Above everything else. A zero-segment path has no `first`, and without this
  // arm it reaches `first !== 'o'` and is reported as a page nobody serves.
  if (parts.length === 0) return { name: 'landing' };
  const [first, second, third, fourth, fifth] = parts;
  const org = typeof second === 'string' && isSlug(second) ? second : null;
  const uuid = (value: string | undefined): string | null =>
    typeof value === 'string' && isUuid(value) ? value : null;

  if (first === 'login' && parts.length === 1) return { name: 'login' };
  if (first === 'invite' && typeof second === 'string' && parts.length === 2) {
    return { name: 'invite', token: second };
  }
  if (first === 'auth' && second === 'callback') return { name: 'callback' };
  if (first !== 'o' || org === null) return { name: 'not-found', path: pathname };

  // The organization is `parts[1]`, so the SECTION is `parts[2]`. Comparing the
  // section name against the organization index matched nothing: every
  // `/o/:org/...` path except the bare skills index fell through to
  // `not-found`, and with it every screen in this application. It typechecked,
  // it passed `bun run deadcode`, and it passed the design gate, because the
  // defect is in which path segment a name is read from and nothing in the
  // repository can see that. `test/router.test.ts` asserts this table against
  // its own inverse in `hrefFor` so the two cannot drift apart again.
  if (third === 'skills' && parts.length === 3) return { name: 'skills', org };
  if (third === 'skills' && parts.length === 4) {
    const id = uuid(fourth);
    return id === null
      ? { name: 'not-found', path: pathname }
      : { name: 'skill', org, id };
  }
  if (third === 'skills' && parts.length === 5 && fifth === 'edit') {
    const id = uuid(fourth);
    return id === null
      ? { name: 'not-found', path: pathname }
      : { name: 'skill-edit', org, id };
  }
  if (third === 'reviews' && parts.length === 4) {
    const draftId = uuid(fourth);
    return draftId === null
      ? { name: 'not-found', path: pathname }
      : { name: 'review', org, draftId };
  }

  if (third === 'connect' && fourth === 'authorize' && parts.length === 4) {
    return { name: 'authorize', org };
  }

  if (parts.length === 3) {
    if (third === 'toolkits') return { name: 'toolkits', org };
    if (third === 'improvements') return { name: 'improvements', org };
    if (third === 'connect') return { name: 'connect', org };
    if (third === 'admin') return { name: 'admin', org };
  }
  return { name: 'not-found', path: pathname };
}

export function hrefFor(route: Route): string {
  switch (route.name) {
    case 'landing':
      return '/';
    case 'login':
      return '/login';
    case 'invite':
      return `/invite/${encodeURIComponent(route.token)}`;
    case 'callback':
      return '/auth/callback';
    case 'skills':
      return `/o/${route.org}/skills`;
    case 'skill':
      return `/o/${route.org}/skills/${route.id}`;
    case 'skill-edit':
      return `/o/${route.org}/skills/${route.id}/edit`;
    case 'review':
      return `/o/${route.org}/reviews/${route.draftId}`;
    case 'toolkits':
      return `/o/${route.org}/toolkits`;
    case 'improvements':
      return `/o/${route.org}/improvements`;
    case 'connect':
      return `/o/${route.org}/connect`;
    case 'authorize':
      return `/o/${route.org}/connect/authorize`;
    case 'admin':
      return `/o/${route.org}/admin`;
    case 'not-found':
      return route.path;
  }
}

/**
 * Where to send the browser after a successful sign-in.
 *
 * Only same-origin absolute paths are accepted. A return path arriving from a
 * query string is attacker-controlled, and a redirect to another origin from a
 * login screen is the oldest open redirect there is. Anything else falls back
 * to the library, which is the documented default landing.
 *
 * BACKSLASHES ARE REFUSED, AND THAT IS THE WHOLE POINT OF THIS FUNCTION
 *   A leading `//` is the obvious way to leave the origin, and this guard
 *   caught it. It did not catch `/\evil.example.com`. WHATWG URL parsing
 *   normalises `\` to `/` in a special-scheme URL, so a value that passes a
 *   plain `startsWith('//')` check is nonetheless resolved by the browser as
 *   `//evil.example.com` and navigates off-origin:
 *
 *     new URL('/\\evil.example.com', 'http://localhost:4173')
 *       -> http://evil.example.com/
 *
 *   The check is therefore on the resolved origin rather than on the string,
 *   because the string is not what the browser acts on. Nothing this
 *   application routes to contains a backslash, so refusing them outright
 *   costs nothing and leaves no second spelling to reason about.
 */
export function safeReturnPath(
  value: string | null | undefined,
  fallback: string,
  origin?: string,
): string {
  if (typeof value !== 'string' || value === '') return fallback;
  if (value.includes('\\')) return fallback;
  // CONTROL CHARACTERS, REFUSED OUTRIGHT, AND THIS IS NOT PARANOIA
  //   WHATWG URL parsing STRIPS tab, LF and CR from a URL before resolving it.
  //   So '/\t/evil.com' is not a path on this origin — it parses as
  //   '//evil.com', which is ANOTHER ORIGIN, while the string still begins
  //   with '/' and passes every check above. Measured: with
  //   `new URL('/\t/evil.com', 'https://the-app.example')` the result is
  //   'https://evil.com/'.
  //
  //   The backslash is refused for the same reason and by the same rule: a
  //   spelling the resolver treats differently from the string. Nothing this
  //   application routes to contains a control character, so refusing them
  //   costs nothing and leaves no second spelling to reason about.
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return fallback;
  }
  if (!value.startsWith('/') || value.startsWith('//')) return fallback;
  if (value.startsWith('/login') || value.startsWith('/auth/')) return fallback;
  // The origin to resolve against. In a browser that is this page's own
  // origin. On the server it is the DEPLOYMENT's public origin, passed in by
  // the caller, and it is passed in because `location` does not exist there:
  // the check below used to run only in a browser, and the server returned the
  // attacker's string unchecked. `url.origin` is not a substitute — it is the
  // Host header the client sent, and a client can send anything.
  const base = origin ?? (typeof location === 'undefined' ? null : location.origin);
  // No origin to check against: refuse rather than pass a value through
  // unchecked. A guard that cannot compare cannot clear.
  if (base === null) return fallback;
  try {
    return new URL(value, base).origin === new URL(base).origin ? value : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Library filters live in the query string so a view is linkable and the
 * browser's back button moves through filter states rather than leaving the
 * screen. The scope is explicit because V1 section 9 requires the default to be
 * usable published skills and "My drafts" to be a deliberate choice.
 */
export interface SkillsQuery {
  readonly q: string;
  readonly owner: string;
  readonly status: string;
  readonly scope: 'published' | 'drafts';
}

export const DEFAULT_SKILLS_QUERY: SkillsQuery = {
  q: '',
  owner: 'all',
  status: 'all',
  scope: 'published',
};

export function parseSkillsQuery(search: string): SkillsQuery {
  const params = new URLSearchParams(search);
  const scope = params.get('scope') === 'drafts' ? 'drafts' : 'published';
  return {
    q: (params.get('q') ?? '').slice(0, 200),
    owner: (params.get('owner') ?? 'all').slice(0, 64),
    status: (params.get('status') ?? 'all').slice(0, 32),
    scope,
  };
}

/** The inverse of `parseSkillsQuery`; defaults are omitted so the url stays short. */
export function skillsQueryToSearch(query: SkillsQuery): string {
  const params = new URLSearchParams();
  if (query.q !== '') params.set('q', query.q);
  if (query.owner !== 'all') params.set('owner', query.owner);
  if (query.status !== 'all') params.set('status', query.status);
  if (query.scope !== 'published') params.set('scope', query.scope);
  const encoded = params.toString();
  return encoded === '' ? '' : `?${encoded}`;
}

export function hasActiveFilters(query: SkillsQuery): boolean {
  return query.q !== '' || query.owner !== 'all' || query.status !== 'all';
}